import React, { useState } from 'react';
import { Check, X, Save } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { generateId } from '../../utils/helpers';
import { api } from '../../utils/api';
import DateInput from '../../components/common/DateInput';

function StaffAttendance() {
  const { attendance, setAttendance, staff, academicYear } = useApp();
  const { currentUser } = useAuth();
  const toast = useToast();
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(today);
  const [localAtt, setLocalAtt] = useState({});
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    const existing = attendance.filter(a => a.date === date && a.entityType === 'staff');
    const map = {};
    existing.forEach(a => { map[a.entityId] = a.status; });
    staff.filter(s=>s.status==='active').forEach(s => { if (!map[s.id]) map[s.id] = 'present'; });
    setLocalAtt(map);
    setSaved(false);
  }, [date]);

  const activeStaff = staff.filter(s => s.status === 'active');
  const setStatus = (id, status) => setLocalAtt(prev => ({ ...prev, [id]: status }));

  const save = async () => {
    const filtered = attendance.filter(a => !(a.date === date && a.entityType === 'staff'));
    const newRecs = activeStaff.map(s => ({
      id: generateId('ATT'), date, entityType: 'staff', entityId: s.id,
      classId: null, status: localAtt[s.id] || 'present', markedBy: currentUser?.profileId || 'STF-001',
      academicYear: academicYear || '',
    }));
    setSaving(true);
    try {
      await api.post('/attendance/bulk', { records: newRecs });
      setAttendance([...filtered, ...newRecs]);
      toast?.success('Attendance saved');
    } catch (e) {
      toast?.error(`Save failed: ${e.message}`);
      setSaving(false);
      return;
    }
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const counts = { present: 0, absent: 0 };
  Object.values(localAtt).forEach(s => { if (s === 'present') counts.present++; else counts.absent++; });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold">Staff Attendance</h1><p className="text-gray-500 text-sm">Mark staff daily attendance</p></div>
        <button onClick={save} disabled={saving} className={`flex items-center gap-2 px-4 py-1 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 ${saved?'bg-green-600 text-white':'bg-indigo-600 hover:bg-indigo-700 text-white'}`}>
          <Save size={16}/>{saving?'Saving...':saved?'Saved!':'Save'}
        </button>
      </div>
      <div className="flex gap-4 mb-6">
        <div>
          <label className="block font-medium mb-1">Date</label>
          <DateInput value={date} onChange={e=>setDate(e.target.value)} className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-green-50 rounded-lg px-4 py-1 text-center border border-green-200"><div className="text-2xl font-bold text-green-700">{counts.present}</div><div className="text-green-600 font-medium">Present</div></div>
        <div className="bg-red-50 rounded-lg px-4 py-1 text-center border border-red-200"><div className="text-2xl font-bold text-red-700">{counts.absent}</div><div className="text-red-600 font-medium">Absent</div></div>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="divide-y divide-gray-50">
          {activeStaff.map((member,i)=>{
            const status = localAtt[member.id]||'present';
            return (
              <div key={member.id} className="flex items-center justify-between px-4 py-1 hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <span className="text-sm w-6">{i+1}</span>
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">{member.firstName?.[0]}{member.lastName?.[0]}</div>
                  <div><p className="text-sm font-medium">{member.firstName} {member.lastName}</p><p className="">{member.designation}</p></div>
                </div>
                <div className="flex gap-2">
                  <button onClick={()=>setStatus(member.id,'present')} className={`flex items-center gap-1 px-3 py-1 rounded-lg font-medium border transition-all ${status==='present'?'bg-green-100 text-green-700 border-green-300':'border-gray-200 hover:bg-gray-100'}`}><Check size={12}/>Present</button>
                  <button onClick={()=>setStatus(member.id,'absent')} className={`flex items-center gap-1 px-3 py-1 rounded-lg font-medium border transition-all ${status==='absent'?'bg-red-100 text-red-700 border-red-300':'border-gray-200 hover:bg-gray-100'}`}><X size={12}/>Absent</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
export default StaffAttendance;
